import { useState } from 'react'
import {
  TextInput,
  NumberInput,
  SelectInput,
  SelectArrayInput,
  SimpleForm,
  required,
  useRecordContext,
} from 'react-admin'
import Grid from '@mui/material/Grid'
import { Tabs, Tab, Box, Typography } from '@mui/material'

import GridContainer from '../../components/GridContainer'
import AiBookToolbar from '../../components/AiBookToolbar'
import CustomFileField from '../../components/CustomFileField'
import CustomFileInput from '../../components/CustomFileInput'
import CustomTextInput from '../../components/CustomTextInput'
import { VOICE_OPTIONS } from '../../components/constants'

import MusicMultiSelectInput from './MusicMultiSelectInput'
import ChaptersTabManager from './ChaptersTabManager'
import SentencesTab from './SentencesTab'
import ReplaceWordTab from './ReplaceWordTab'
import VoiceMultiSelectInput from './VoiceMultiSelectInput'

const AiBooksForm = ({ ...props }) => {
  const recordContext = useRecordContext()
  const isCreating = !recordContext?.id

  const [tabValue, setTabValue] = useState(0)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }
  return (
    <SimpleForm toolbar={<AiBookToolbar />} {...props}>
      <GridContainer>
        <Grid item xs={12} md={6}>
          <TextInput
            source="title"
            label="Title"
            fullWidth
            validate={required()}
            variant="standard"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            source="id"
            label="ID"
            fullWidth
            variant="standard"
            disabled={true}
            helperText={isCreating ? 'Auto-generated from title' : ''}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomFileInput
            source="pdfFile"
            label="Upload PDF File"
            accept=".pdf"
            validate={isCreating ? required() : undefined}
            type="ai-book-pdf"
            upload
            showProgress>
            <CustomFileField source="src" title="title" />
          </CustomFileInput>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            source="sourcePdfPath"
            label="Source PDF Path"
            fullWidth
            variant="standard"
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="chapterCount"
            label="Chapter Count"
            fullWidth
            variant="standard"
            min={0}
            disabled
          />
        </Grid>
        {/*<Grid item xs={12} md={6}>*/}
        {/*  <NumberInput*/}
        {/*    source="completedChapters"*/}
        {/*    label="Completed Chapters"*/}
        {/*    fullWidth*/}
        {/*    variant="standard"*/}
        {/*    min={0}*/}
        {/*    disabled*/}
        {/*  />*/}
        {/*</Grid>*/}
        <Grid item xs={12} md={6}>
          <SelectInput
            source="status"
            label="Status"
            choices={[
              { id: 'new', name: 'New' },
              { id: 'error', name: 'Error' },
              { id: 'text_extracting', name: 'Text Extracting' },
              { id: 'text_extracted', name: 'Text Extracted' },
              { id: 'tts_generated', name: 'TTS Generated' },
              { id: 'completed', name: 'Completed' },
            ]}
            fullWidth
            variant="standard"
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <VoiceMultiSelectInput
            source="voices"
            label="Voices"
            choices={VOICE_OPTIONS || []}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <CustomTextInput
            name="summary"
            source="summary"
            fullWidth
            multiline
            rows={5}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            source="partner"
            label="Partner"
            fullWidth
            variant="standard"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            source="translator"
            label="Translator"
            fullWidth
            variant="standard"
          />
        </Grid>

        {/* Sample Chapters multi-select (edit only) */}
        <Grid item xs={12} md={6}>
          <SelectArrayInput
            source="sampleChapterOrderIndexes"
            label="Sample Chapters"
            fullWidth
            variant="standard"
            disabled={isCreating}
            choices={(recordContext?.chapters || []).map(ch => ({
              orderIndex: ch.orderIndex,
              name: ch.title,
            }))}
            optionText="name"
            optionValue="orderIndex"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SelectInput
            source="isExclusive"
            label="Is Exclusive"
            choices={[
              { id: true, name: 'True' },
              { id: false, name: 'False' },
            ]}
            fullWidth
            variant="standard"
          />
        </Grid>

        {/* Sample Text & Outro Text Section */}
        <Grid item xs={12} md={6}>
          <CustomTextInput
            name="sampleText"
            source="sampleText"
            label="Sample Outro"
            fullWidth
            multiline
            rows={4}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextInput
            name="outroText"
            source="outroText"
            label="Audiobook Outro"
            fullWidth
            multiline
            rows={4}
          />
        </Grid>

        {/* Music Selection Section */}
        <Grid item xs={12}>
          <Box
            sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Music Selection
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Intro
                </Typography>
                <MusicMultiSelectInput
                  source="introMusicPath"
                  label="Select Intro Music"
                  type="Intro"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Outro
                </Typography>
                <MusicMultiSelectInput
                  source="outroMusicPath"
                  label="Select Outro Music"
                  type="Outro"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Chapter
                </Typography>
                <MusicMultiSelectInput
                  source="chapterMusicPath"
                  label="Select Chapter Music"
                  type="Chapter"
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Management Tabs Section */}
        <Grid item xs={12}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="book management tabs">
                <Tab label="Chapters" />
                <Tab label="All Sentences" />
                <Tab label="Replace Word" />
              </Tabs>
            </Box>
            <Box sx={{ mt: 2 }}>
              {tabValue === 0 && <ChaptersTabManager />}
              {tabValue === 1 && <SentencesTab />}
              {tabValue === 2 && <ReplaceWordTab />}
            </Box>
          </Box>
        </Grid>
      </GridContainer>
    </SimpleForm>
  )
}

export default AiBooksForm
